import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Puzzle } from "@gen/logic-grid/types.ts";
import { loadPuzzle, PuzzleNotFound } from "../lib/archive.ts";
import { explainClue } from "../lib/explain.ts";
import { type Hint, hasMoreStages, hintLines, nextHint, type Stage } from "../lib/hint.ts";
import { drawnBlocks } from "@gen/logic-grid/axes.ts";
import {
  type Cell,
  cellKey,
  findMistakes,
  isSolved,
  type Mark,
  type Marks,
  blocksWithMarks,
  resolve,
  setMark,
  transcribe,
} from "../lib/marks.ts";
import BlockMap from "./BlockMap.tsx";
import PuzzleGrid, { PuzzleBlock } from "./PuzzleGrid.tsx";
import { Button, Note, sectionHead } from "./ui/index.ts";

/**
 * Which mark the next tap writes.
 *
 * One control with two positions, not two controls. Two buttons made the
 * reader work out that they were a pair and that exactly one of them was on;
 * a switch says both of those things by being one object with a thing sliding
 * about in it, and says them without a caption.
 *
 * `role="switch"` rather than a radiogroup, for the same reason: there is one
 * question here, not a list, and the answer is which way it is thrown. Both
 * glyphs stay visible so the reader can see what the other position holds.
 */
function MarkPicker({ active, onPick }: { active: Mark; onPick: (mark: Mark) => void }) {
  const tick = active === "tick";
  return (
    <button
      type="button"
      role="switch"
      aria-checked={tick}
      aria-label="Write ticks instead of crosses"
      onClick={() => onPick(tick ? "cross" : "tick")}
      // Upright beside the grid on a wide screen, lying down above it on a
      // phone: the same rotation the rest of this layout makes at that width.
      className="relative shrink-0 flex split:flex-col items-center cursor-pointer rounded-box border border-line bg-panel p-[3px] gap-[3px] focus-visible:outline-2 focus-visible:outline-rust hover:border-rust"
    >
      {/*
        The knob, moved by transform rather than by reordering anything, so the
        two glyphs never shift and only the block of colour travels.
      */}
      <span
        aria-hidden
        className={`absolute left-[3px] top-[3px] w-[38px] h-[38px] rounded-[2px] bg-rust transition-transform duration-150 ${
          tick ? "translate-x-[41px] split:translate-x-0 split:translate-y-[41px]" : ""
        }`}
      />
      {(["cross", "tick"] as const).map((mark) => (
        <span
          key={mark}
          aria-hidden
          className={`relative w-[38px] h-[38px] flex items-center justify-center text-[1.15rem] leading-none font-bold ${
            active === mark ? "text-panel" : "text-muted"
          }`}
        >
          {mark === "tick" ? "✓" : "✕"}
        </span>
      ))}
    </button>
  );
}

/**
 * One puzzle, open for help.
 *
 * A reader gets here by scanning the code in the front of a book, so they are
 * on a phone, mid-solve, with the book open at a numbered page. They are not
 * here to solve the puzzle on a screen — they are here because the one on paper
 * has stopped moving. So the grid opens empty and they copy in only what they
 * are confident about, and the whole point of the exercise is the hint button.
 *
 * The puzzle comes from the archive rather than from a seed. See lib/archive.ts
 * for why that is not negotiable.
 */
/**
 * Where a reader's marks are kept between visits, when the caller asks for it.
 *
 * Keyed by the puzzle's own id rather than by book and number, so the write
 * that happens while a different puzzle is loading still lands on the puzzle
 * those marks actually belong to.
 */
function marksKey(id: string): string {
  return `wh:marks:${id}`;
}

function readMarks(id: string): Marks {
  try {
    const raw = localStorage.getItem(marksKey(id));
    return raw ? (JSON.parse(raw) as Marks) : {};
  } catch {
    // Private windows and blocked site data both throw. Losing progress is a
    // worse page, not a broken one.
    return {};
  }
}

function writeMarks(id: string, marks: Marks): void {
  try {
    localStorage.setItem(marksKey(id), JSON.stringify(marks));
  } catch {
    /* see readMarks */
  }
}

export default function PuzzleHelp({
  slug,
  number,
  remember = false,
  exportable = false,
}: {
  slug: string;
  number: number;
  /** Keep the reader's marks in this browser between visits. */
  remember?: boolean;
  /**
   * Offer the marks as text. Workbench only: a reader has the grid in front of
   * them, and this exists so a position can be reported somewhere it can be
   * read back — a bug report, a message, a test case — instead of screenshotted
   * and squinted at.
   */
  exportable?: boolean;
}) {
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const [error, setError] = useState<"not-found" | "failed" | null>(null);
  const [marks, setMarks] = useState<Marks>({});
  const [hint, setHint] = useState<Hint | null>(null);
  const [stage, setStage] = useState<Stage>(1);
  const [checked, setChecked] = useState<Set<string> | null>(null);
  /**
   * Which mark a tap writes. Cross by default: every tick forces 2(M-1) crosses
   * around it, so a finished grid is mostly crosses and that is the mark a
   * reader reaches for most.
   */
  const [writing, setWriting] = useState<Mark>("cross");
  /** The transcript, once asked for. Held on screen when the clipboard says no. */
  const [transcript, setTranscript] = useState<string | null>(null);
  /*
   * One block at a time, or the whole staircase. Null means nobody has chosen,
   * so `fits` below decides — by measurement rather than by screen width, since
   * what matters is whether this particular grid fits this particular window.
   * A 4x4 grid is fine on a phone and an Expert grid is not fine on a laptop.
   */
  const [chosenMode, setChosenMode] = useState<"whole" | "block" | null>(null);
  const [block, setBlock] = useState<[number, number]>([0, 1]);
  /*
   * How wide the staircase wants to be, measured once while it is on screen,
   * and how much room the window has. Kept apart on purpose.
   *
   * The obvious thing — watch the grid and see whether it overflows — has a
   * trap in it: the moment it does not fit, the staircase unmounts, and the
   * observer goes with it. Nothing is left watching, so widening the window
   * again never brings it back. The staircase's width does not depend on the
   * window, so measuring it once is enough, and the comparison can then be
   * made from the window alone however either of them changes.
   */
  const [natural, setNatural] = useState<number | null>(null);
  const [room, setRoom] = useState(() =>
    typeof document === "undefined" ? 0 : document.documentElement.clientWidth,
  );
  const gauge = useRef<HTMLDivElement | null>(null);
  /*
   * Swipe, sideways only. A vertical swipe is the page scrolling and there is
   * no winning that argument, so the gesture is ignored unless it is clearly
   * horizontal. It is an accelerator and never the only way through: the arrows
   * and the map do the same job for a mouse, a keyboard, or a reader who never
   * discovers that swiping does anything.
   */
  const swipeFrom = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    let live = true;
    setPuzzle(null);
    setError(null);
    setMarks({});
    setHint(null);
    setChecked(null);
    setTranscript(null);
    setChosenMode(null);
    setBlock([0, 1]);
    setNatural(null);
    loadPuzzle(slug, number)
      .then((p) => {
        if (!live) return;
        setPuzzle(p);
        if (remember) setMarks(readMarks(p.id));
      })
      .catch((cause) => {
        if (live) setError(cause instanceof PuzzleNotFound ? "not-found" : "failed");
      });
    return () => {
      live = false;
    };
  }, [slug, number, remember]);

  /*
   * Saved against the loaded puzzle, not against the slug and number in props.
   * On the render where those props change, this effect still sees the puzzle
   * that is on screen — so keying off the puzzle means the save lands on the
   * right one instead of stamping the outgoing marks onto the incoming puzzle.
   */
  useEffect(() => {
    if (!remember || !puzzle) return;
    writeMarks(puzzle.id, marks);
  }, [remember, puzzle, marks]);

  /*
   * Any change to the grid retires the hint on screen. It was advice about a
   * position that no longer exists, and leaving it up invites the reader to
   * write a mark that was right one click ago.
   */
  const toggle = useCallback(
    (cell: Cell) => {
      if (!puzzle) return;
      setMarks((current) => setMark(current, cell, writing));
      setHint(null);
      setStage(1);
      setChecked(null);
      setTranscript(null);
    },
    [puzzle, writing],
  );

  const solved = useMemo(() => (puzzle ? isSolved(puzzle, marks) : false), [puzzle, marks]);
  /** What the grid draws: the reader's marks plus the crosses their ticks imply. */
  const shown = useMemo(() => (puzzle ? resolve(puzzle, marks) : {}), [puzzle, marks]);

  /*
   * Does the staircase fit the window it has been given?
   *
   * Measured, not guessed from a breakpoint. The two get different answers all
   * the time: a 4x4 grid is comfortable on a phone, and a 6x6 one is 1102px
   * wide and does not fit a laptop. A media query would send the first to the
   * cramped view and leave the second in the one it overflows.
   *
   * The staircase renders first and this switches away from it if it did not
   * fit, so the reader whose grid does fit never sees anything happen.
   */
  useEffect(() => {
    const host = gauge.current;
    if (!host) return;
    const scroller = host.firstElementChild;
    if (!(scroller instanceof HTMLElement)) return;
    const table = scroller.querySelector("table");
    if (!table) return;
    // The table plus the gutters it sits in. Not `scrollWidth`, which reports
    // the scroller's own width whenever the content is comfortably inside it.
    const style = getComputedStyle(scroller);
    const gutters = Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight);
    setNatural(table.getBoundingClientRect().width + gutters);
  }, [puzzle]);

  /*
   * Watched rather than listened for. A `resize` event only fires when the
   * window itself changes, and the room this page has can change without that
   * happening — a zoom, a devtools pane, a browser that changes the viewport
   * without telling anyone. Observing the document element catches all of it.
   *
   * `clientWidth`, not `innerWidth`: it leaves out the vertical scrollbar,
   * which is width the grid does not get.
   */
  useEffect(() => {
    const root = document.documentElement;
    const measure = () => setRoom(root.clientWidth);
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(root);
    return () => watch.disconnect();
  }, []);

  const fits = natural === null || natural <= room;
  const mode = chosenMode ?? (fits ? "whole" : "block");
  const order = puzzle ? drawnBlocks(puzzle.categories.length) : [];
  const blocksMarked = useMemo(
    () => (puzzle ? blocksWithMarks(puzzle, marks) : new Set<string>()),
    [puzzle, marks],
  );

  /** Step through the blocks in printed order: the spine first, then the rest. */
  const step = (by: number) => {
    const at = order.findIndex(([i, j]) => i === block[0] && j === block[1]);
    const next = order[(at + by + order.length) % order.length];
    if (next) setBlock(next);
  };

  if (error === "not-found") {
    return (
      <Note>
        <p className="mb-0">
          <strong>No puzzle {number} in that book.</strong> Check the number printed on the page —
          and that you have picked the right book above.
        </p>
      </Note>
    );
  }
  if (error === "failed") {
    return (
      <Note>
        <p className="mb-0">
          <strong>That did not load.</strong> Something went wrong fetching the puzzle. Trying again
          in a moment usually sorts it.
        </p>
      </Note>
    );
  }
  if (!puzzle) {
    return (
      <p className="text-muted" aria-live="polite">
        Loading puzzle {number}…
      </p>
    );
  }

  /*
   * The reasoning behind the hint on screen, worked out once and used twice —
   * to decide whether there is a fourth stage to offer, and to print it. Null
   * for a grid hint, whose reason is already given at stage two, and for the
   * occasional clue whose route does not shorten into a sentence.
   */
  const why =
    hint && hint.kind === "clue"
      ? explainClue(puzzle, shown, hint.clue, hint.cell, hint.mark)
      : null;

  const askHint = () => {
    if (hint && hasMoreStages(hint, stage, why)) {
      setStage((s) => Math.min(4, s + 1) as Stage);
      return;
    }
    const next = nextHint(puzzle, marks);
    setHint(next);
    setStage(1);
    /*
     * Go to the block the hint is about. Without this, a hint in block mode
     * names a square the reader cannot see and asks them to go and find it,
     * which is the same failure as naming a clue and not saying which one.
     */
    if ("cell" in next) setBlock([next.cell.rowCat, next.cell.colCat]);
  };

  const onSwipeStart = (e: React.PointerEvent) => {
    swipeFrom.current = { x: e.clientX, y: e.clientY };
  };
  const onSwipeEnd = (e: React.PointerEvent) => {
    const from = swipeFrom.current;
    swipeFrom.current = null;
    if (!from) return;
    const dx = e.clientX - from.x;
    const dy = e.clientY - from.y;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 2) return;
    step(dx < 0 ? 1 : -1);
  };

  const copyMarks = async () => {
    const text = transcribe(puzzle, marks, { book: slug, puzzle: number });
    try {
      await navigator.clipboard.writeText(text);
      setTranscript(null);
    } catch {
      setTranscript(text);
    }
  };

  const check = () => {
    setChecked(new Set(findMistakes(puzzle, marks).map((m) => cellKey(m.cell))));
    setHint(null);
    setStage(1);
  };

  const marked = Object.keys(marks).length;
  const lines = hint ? hintLines(puzzle, hint, stage, why) : [];

  return (
    <div className="mt-[1.6rem]">
      {/*
        The number and nothing else. `puzzle.theme` is an internal id
        ("platform-girton"), not the title the book prints, and the title is not
        in the archive to look up — it is composed at render time. Resolving one
        would mean importing the whole theme catalogue into the browser for a
        string, through a lookup that throws on an id an older book might carry.
        The reader has the book open in front of them regardless.
      */}
      <h3 className={`${sectionHead} text-[clamp(1.3rem,3vw,1.6rem)]`}>Puzzle {number}</h3>

      {/*
        No clue list. The reader is holding the book — the clues are printed on
        the page in front of them, and a second copy on screen is a column of
        text competing with the only thing here they cannot get on paper. A
        clue hint names its number, which is all they need to find it; stage two
        quotes the one line, so they can tell at a glance they are looking at
        the right one.
      */}
      {mode === "block" ? (
        <>
          {/*
            The picker sits above the block rather than below it, because on a
            phone the block fills the screen and a control under it would be off
            the bottom — and this is the control the reader uses most.
          */}
          <div className="flex gap-[0.7rem] items-center justify-center flex-wrap mb-[0.8rem]">
            {/*
              The name goes in the markup rather than in an `aria-label`, which
              the shared Button does not forward. A chevron with no accessible
              name is a button that announces itself as nothing at all.
            */}
            <Button ghost onClick={() => step(-1)}>
              <span aria-hidden>‹</span>
              <span className="sr-only">Previous block</span>
            </Button>
            <BlockMap puzzle={puzzle} current={block} marked={blocksMarked} onPick={setBlock} />
            <Button ghost onClick={() => step(1)}>
              <span aria-hidden>›</span>
              <span className="sr-only">Next block</span>
            </Button>
          </div>
          <div
            className="flex gap-[clamp(1rem,3vw,2rem)] items-start flex-wrap justify-center"
            onPointerDown={onSwipeStart}
            onPointerUp={onSwipeEnd}
          >
            <MarkPicker active={writing} onPick={setWriting} />
            <div className="min-w-0">
              <PuzzleBlock
                puzzle={puzzle}
                block={block}
                marks={shown}
                wrong={checked ?? undefined}
                highlight={hint && stage >= 3 && "cell" in hint ? hint.cell : null}
                onToggle={toggle}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="flex gap-[clamp(1rem,3vw,2rem)] items-start flex-wrap justify-center">
          <MarkPicker active={writing} onPick={setWriting} />
          <div className="min-w-0 w-full" ref={gauge}>
            <PuzzleGrid
              puzzle={puzzle}
              marks={shown}
              wrong={checked ?? undefined}
              highlight={hint && stage >= 3 && "cell" in hint ? hint.cell : null}
              onToggle={toggle}
            />
          </div>
        </div>
      )}
      <div className="flex gap-[0.7rem] flex-wrap mt-[1.6rem]">
        <Button onClick={askHint} disabled={solved}>
          {hint && hasMoreStages(hint, stage, why)
            ? stage === 3
              ? "Show me why"
              : "Tell me more"
            : "Give me a hint"}
        </Button>
        <Button onClick={check} ghost disabled={marked === 0}>
          Check my grid
        </Button>
        <Button onClick={() => setChosenMode(mode === "block" ? "whole" : "block")} ghost>
          {mode === "block" ? "Whole grid" : "One block"}
        </Button>
        {marked > 0 ? (
          <Button
            onClick={() => {
              setMarks({});
              setHint(null);
              setStage(1);
              setChecked(null);
              setTranscript(null);
            }}
            ghost
          >
            Clear
          </Button>
        ) : null}
        {exportable && marked > 0 ? (
          <Button onClick={() => void copyMarks()} ghost>
            Copy my marks
          </Button>
        ) : null}
      </div>

      {/*
        Shown only when the copy did not happen. Reading it back off the screen
        is worse than a clipboard and better than nothing, and the clipboard API
        refuses often enough — no permission, no secure context, a browser that
        wants a fresher user gesture — that silently failing would be the wrong
        way round.
      */}
      {transcript !== null ? (
        <div className="mt-[1rem]">
          <p className="text-muted text-[0.85rem] mb-[0.4rem]">
            The clipboard was not available, so here it is to copy by hand.
          </p>
          <textarea
            readOnly
            className="w-full h-[14rem] border border-line rounded-box p-[0.6rem] bg-panel font-mono text-[0.75rem]"
            value={transcript}
            onFocus={(e) => e.currentTarget.select()}
          />
        </div>
      ) : null}

      {/*
        One live region for everything the page says back, so a screen reader
        hears a hint or a check result without having to go looking for it.
      */}
      <div aria-live="polite" className="mt-[1.2rem]">
        {solved ? (
          <Note>
            <p className="mb-0">
              <strong>That is the whole grid.</strong> Every mark in it agrees with the answer key.
            </p>
          </Note>
        ) : null}

        {/*
          Only when no hint is on screen. A hint about a grid with a mistake in
          it says the same thing and says it better — it names the block — so
          showing both reads as the page repeating itself. The highlights in the
          grid come from `checked` either way and stay put.
        */}
        {checked && !solved && !hint ? (
          <Note>
            <p className="mb-0">
              {checked.size === 0 ? (
                <>
                  <strong>All {marked} marks look right.</strong> Nothing you have written
                  contradicts the answer. Keep going.
                </>
              ) : (
                <>
                  <strong>
                    {checked.size} mark{checked.size === 1 ? "" : "s"} cannot be right.
                  </strong>{" "}
                  Highlighted in the grid.
                </>
              )}
            </p>
          </Note>
        ) : null}

        {lines.length > 0 && !solved ? (
          <Note>
            {lines.map((line, i) => (
              <p key={line} className={i === lines.length - 1 ? "mb-0" : undefined}>
                {line}
              </p>
            ))}
          </Note>
        ) : null}
      </div>
    </div>
  );
}
